import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { MiDetailFields, MiIcon, MiListCards } from "@miplatform/components";
import {
  ICONS_WEB,
  INPUT_TYPES,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { createUUID, getContrastColor, isEmpty } from "@miplatform/utils";
import { useTheme } from "@emotion/react";
import * as mdi from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { IssueCard } from "../../views/Schedule/Scheduler/CloseoutDrawer/IssueCard/IssueCard";
import { Footer } from "../Footer/Footer";
import { useAssignmentSummaryViewModel } from "./useAssignmentSummaryViewModel";

const TECHNICIAN_MANAGEMENT = {
  SINGLE_TECHNICIAN: "Single Technician",
  MULTI_TECHNICIAN: "Multi Technicians",
};

const TECHNICIAN_OPTIONS = [
  {
    label: TECHNICIAN_MANAGEMENT.SINGLE_TECHNICIAN,
    value: TECHNICIAN_MANAGEMENT.SINGLE_TECHNICIAN,
  },
  {
    label: TECHNICIAN_MANAGEMENT.MULTI_TECHNICIAN,
    value: TECHNICIAN_MANAGEMENT.MULTI_TECHNICIAN,
  },
];

const ASSIGNMENT_SCHEDULE = {
  DIFFERENT_TIME: "Different Time",
  SAME_TIME: "Same Time",
};

const SCHEDULE_OPTIONS = [
  {
    label: ASSIGNMENT_SCHEDULE.DIFFERENT_TIME,
    value: ASSIGNMENT_SCHEDULE.DIFFERENT_TIME,
  },
  {
    label: ASSIGNMENT_SCHEDULE.SAME_TIME,
    value: ASSIGNMENT_SCHEDULE.SAME_TIME,
  },
];

const IssueListItem = ({ item: issue, index }) => (
  <IssueCard issue={issue} index={index} />
);

export const AssignmentSummary = ({ assignment = {}, onClose = () => {} }) => {
  const {
    assignmentNumber = "",
    order: { orderTemplateAbbreviation, orderTemplateName },
    multiTechnician,
    multiTechnicianSameTime,
    id: assignmentId,
  } = assignment;
  const assignmentTemplateColor = assignment?.order?.orderTemplateColor;

  const theme = useTheme();
  const edit = useEdit({
    isMultiTechnician: multiTechnician
      ? TECHNICIAN_MANAGEMENT.MULTI_TECHNICIAN
      : TECHNICIAN_MANAGEMENT.SINGLE_TECHNICIAN,
    isMultiTechnicianSameTime:
      multiTechnicianSameTime && ASSIGNMENT_SCHEDULE.SAME_TIME,
  });

  useEffect(() => {
    if (
      edit.getValue("isMultiTechnician") ===
      TECHNICIAN_MANAGEMENT.SINGLE_TECHNICIAN
    ) {
      edit.update({
        isMultiTechnicianSameTime: undefined,
      });
    }
  }, [edit.getValue("isMultiTechnician")]);

  const detailFields = [
    {
      label: "Technician Management",
      name: "isMultiTechnician",
      width: "50%",
      fieldType: INPUT_TYPES.SELECT,
      required: true,
      options: TECHNICIAN_OPTIONS,
    },
    {
      label: "Assignment Schedule",
      name: "isMultiTechnicianSameTime",
      width: "50%",
      fieldType: INPUT_TYPES.SELECT,
      disabled:
        edit.getValue("isMultiTechnician") !==
        TECHNICIAN_MANAGEMENT.MULTI_TECHNICIAN,
      required:
        edit.getValue("isMultiTechnician") ===
        TECHNICIAN_MANAGEMENT.MULTI_TECHNICIAN,
      options: SCHEDULE_OPTIONS,
    },
  ];

  const { isLoading, createAttemptForMultiTechnician } =
    useAssignmentSummaryViewModel({
      onClose,
    });

  const handleSaveClick = () => {
    const isMultiTechnician =
      edit.getValue("isMultiTechnician") ===
      TECHNICIAN_MANAGEMENT.MULTI_TECHNICIAN;

    if (isMultiTechnician) {
      createAttemptForMultiTechnician({
        assignmentId,
        data: {
          id: createUUID(),
          multiTechnicianSameTime:
            edit.getValue("isMultiTechnicianSameTime") ===
            ASSIGNMENT_SCHEDULE.SAME_TIME,
        },
      });
    }
  };

  const { classes } = useStyles({ assignmentTemplateColor });

  return (
    <Box className={classes.root} data-testid="create-attempt-request">
      <Box className={classes.mainContainer}>
        <Box className={classes.headerContainer}>
          <MiIcon
            className={classes.headerIcon}
            path={mdi[ICONS_WEB.TOOLS]}
            size={0.8}
            color={theme.palette.common.white}
          />
          <Typography fontWeight={theme.typography.fontWeightBold}>
            Assignment Summary
          </Typography>
        </Box>
        <Box className={classes.assignmentContainer}>
          <Box className={classes.headerContainer}>
            <Box className={classes.abbreviationContainer}>
              <Typography
                variant={TYPOGRAPHY_VARIANTS.CAPTION}
                fontSize={theme.fontSize.xsmall}
              >
                {orderTemplateAbbreviation}
              </Typography>
            </Box>
            <Typography fontWeight={theme.typography.fontWeightBold}>
              {`Assignment #${assignmentNumber}${
                assignment.attemptNumber ? `-${assignment.attemptNumber}` : ""
              }: ${orderTemplateName}`}
            </Typography>
          </Box>
          <MiDetailFields detailFields={detailFields} edit={edit} />
          <Box className={classes.issueList}>
            <MiListCards
              testID="issues-list"
              data={assignment?.issues ?? []}
              CardComponent={IssueListItem}
            />
          </Box>
        </Box>
      </Box>
      <Footer
        show
        sticky
        height={50}
        saveTitle="Save"
        cancelTitle="Close"
        disableSave={
          isLoading ||
          edit.getValue("isMultiTechnician") !==
            TECHNICIAN_MANAGEMENT.MULTI_TECHNICIAN ||
          isEmpty(edit.getValue("isMultiTechnicianSameTime"))
        }
        handleSaveClick={handleSaveClick}
        handleCancelClick={onClose}
      />
    </Box>
  );
};

const useStyles = makeStyles()((theme, { assignmentTemplateColor } = {}) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  mainContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  headerContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  headerIcon: {
    backgroundColor: theme.palette.cyan.dark,
    borderRadius: "100%",
    padding: theme.spacing(1),
  },
  assignmentContainer: {
    backgroundColor: theme.palette.grey.mediumLight,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.cyan.dark}`,
  },
  abbreviationContainer: {
    backgroundColor: assignmentTemplateColor,
    borderRadius: theme.spacing(1),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(0.5, 1),
    color: getContrastColor(assignmentTemplateColor),
  },
  issueList: {
    padding: theme.spacing(2),
  },
}));
